// /sms-sablony — Modul 6: editor SMS sablon (sheet "SMS_Sablony").
// Owner/manager UI pro editaci textu SMS upominek po splatnosti.
//
// Cron `scheduled-sms-upominky` cte sablony, render placeholdery a posila SMS.
// Stupne 1, 3, 5 = pocet dni po splatnosti faktury.
//
// Features:
//   - Tabulka typu sablon (Permanentka / Koucink / Zkusebni / Vychozi / vlastni)
//   - Klik na radek -> expand inline edit form (3 textareas + klicova slova)
//   - Tlacitka "Vlozit placeholder" nad kazdou textareou
//   - Live preview se sample daty
//   - Pridat novy typ (modal)
//   - Smazat (jen owner) + confirm dialog

import React, { useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare, Plus, RefreshCw, AlertTriangle, ArrowLeft, Search, X, Trash2,
  CheckCircle2, ChevronDown, ChevronUp, Save, Eye, Hash, Tag,
} from "lucide-react";
import Card from "../components/Card.jsx";
import GradientHeader from "../components/GradientHeader.jsx";
import InfoTip from "../components/InfoTip.jsx";
import { useApiData, apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import { toast } from "../components/Toast.jsx";

// Sample data pro live preview
const SAMPLE_DATA = {
  jmeno: "Jan",
  castka: "4 990 Kč",
  cisloFaktury: "2026123",
  datumSplatnosti: "30.4.2026",
  dniPoSplatnosti: "5",
};

function renderPreview(text) {
  if (!text) return "";
  return String(text)
    .replace(/\{jmeno\}/g, SAMPLE_DATA.jmeno)
    .replace(/\{castka\}/g, SAMPLE_DATA.castka)
    .replace(/\{cisloFaktury\}/g, SAMPLE_DATA.cisloFaktury)
    .replace(/\{datumSplatnosti\}/g, SAMPLE_DATA.datumSplatnosti)
    .replace(/\{dniPoSplatnosti\}/g, SAMPLE_DATA.dniPoSplatnosti);
}

function findUnknownPlaceholders(text, allowed) {
  if (!text) return [];
  const matches = String(text).match(/\{([^}]+)\}/g) || [];
  const unknown = [];
  for (const m of matches) {
    const name = m.slice(1, -1).trim();
    if (!allowed.includes(name)) unknown.push(m);
  }
  return [...new Set(unknown)];
}

// Confirm dialog (kopie z LogStBonus)
function ConfirmDialog({ title, message, confirmLabel, cancelLabel, variant, onConfirm, onCancel }) {
  const tone = variant === "danger"
    ? { btn: "bg-rose-600 hover:bg-rose-700", icon: AlertTriangle, iconColor: "text-rose-600", iconBg: "bg-rose-100" }
    : { btn: "bg-brand-600 hover:bg-brand-700", icon: CheckCircle2, iconColor: "text-brand-600", iconBg: "bg-brand-100" };
  const Icon = tone.icon;
  return (
    <div className="fixed inset-0 z-[60] bg-brand-dark/50 backdrop-blur-sm flex items-center justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-10 h-10 rounded-full ${tone.iconBg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${tone.iconColor}`} />
          </div>
          <div className="flex-1 pt-1">
            <h3 className="text-lg font-bold text-slate-900 mb-1">{title}</h3>
            <div className="text-sm text-slate-600">{message}</div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button onClick={onCancel} className="btn btn-secondary">
            {cancelLabel || "Zrušit"}
          </button>
          <button onClick={onConfirm} className={`px-4 py-2 text-sm font-semibold text-white rounded-xl transition-colors ${tone.btn}`}>
            {confirmLabel || "Potvrdit"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Editor jednoho stupně (textarea + placeholder buttons + preview + char counter + warning)
function StupenEditor({ stupen, label, value, onChange, allowedPlaceholders }) {
  const textareaRef = useRef(null);
  const insertPlaceholder = (ph) => {
    const ta = textareaRef.current;
    if (!ta) { onChange((value || "") + `{${ph}}`); return; }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = (value || "").substring(0, start);
    const after = (value || "").substring(end);
    const newVal = before + `{${ph}}` + after;
    onChange(newVal);
    setTimeout(() => {
      ta.focus();
      const cursor = start + `{${ph}}`.length;
      ta.setSelectionRange(cursor, cursor);
    }, 0);
  };
  const unknown = findUnknownPlaceholders(value, allowedPlaceholders);
  const preview = renderPreview(value);
  const len = (value || "").length;
  const previewLen = preview.length;
  // SMS limit: 160 znaků GSM-7, 70 znaků UCS-2 (dlouhý znak český → 2 SMS když přes 70)
  const overLimit = previewLen > 160;

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-50 px-3 py-2 flex items-center justify-between border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-100 text-brand-700 text-xs font-bold">{stupen}</span>
          <span className="text-sm font-medium text-slate-700">{label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {allowedPlaceholders.map((ph) => (
            <button key={ph} type="button" onClick={() => insertPlaceholder(ph)}
              className="btn btn-secondary"
              title={`Vložit placeholder {${ph}} na pozici kurzoru`}>
              {`{${ph}}`}
            </button>
          ))}
        </div>
      </div>
      <textarea
        ref={textareaRef}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder={`Text SMS pro stupeň ${stupen}…`}
        className="w-full px-3 py-2 text-sm border-0 focus:outline-none focus:ring-2 focus:ring-brand-100 resize-y min-h-[80px]"
      />
      {value && (
        <div className="border-t border-slate-100 bg-brand-50/30 px-3 py-2">
          <div className="flex items-center gap-1.5 text-xs text-brand-700 font-medium mb-1">
            <Eye className="w-3 h-3" /> Náhled (sample data)
          </div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap">{preview}</div>
        </div>
      )}
      <div className="border-t border-slate-100 bg-slate-50 px-3 py-1.5 flex items-center justify-between text-xs">
        <div className="flex items-center gap-3">
          <span className="text-slate-500">Šablona: <span className="tabular-nums font-medium text-slate-700">{len}</span> znaků</span>
          <span className={overLimit ? "text-rose-600 font-medium" : "text-slate-500"}>
            Po nahrazení: <span className="tabular-nums font-medium">{previewLen}</span> / 160
            {overLimit && " (přesah!)"}
          </span>
        </div>
        {unknown.length > 0 && (
          <div className="text-rose-600 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Nepovolené: {unknown.join(", ")}
          </div>
        )}
      </div>
    </div>
  );
}

// Modal pro pridani noveho typu sablony
function AddModal({ onClose, onSubmit, submitting, error, allowedPlaceholders, existingTypy }) {
  const [typ, setTyp] = useState("");
  const [klicovaSlova, setKlicovaSlova] = useState("");
  const [text1, setText1] = useState("");
  const [text3, setText3] = useState("");
  const [text5, setText5] = useState("");

  const typLower = typ.trim().toLowerCase();
  const typExists = typLower && existingTypy.some((t) => t.toLowerCase() === typLower);
  const canSubmit = typ.trim().length > 0 && !typExists && (text1 || text3 || text5);

  return (
    <div className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden my-6" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-brand-200 bg-gradient-to-r from-slate-50 to-white flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center">
              <Plus className="w-5 h-5 text-brand-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Přidat novou SMS šablonu</h2>
              <p className="text-xs text-slate-500">Cron `smsUpominky` matchuje typ podle klíčových slov v položce faktury</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-slate-100 shrink-0 min-h-[44px] sm:min-h-0" aria-label="Zavřít">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block text-sm">
              <div className="text-slate-700 font-medium mb-1.5">Typ <span className="text-rose-600">*</span></div>
              <input type="text" value={typ} onChange={(e) => setTyp(e.target.value)}
                placeholder="např. Online program"
                className="field w-full" />
              {typExists && <div className="text-xs text-rose-600 mt-1">Typ "{typ}" už existuje. Edituj v tabulce, nebo vyber jiný název.</div>}
            </label>
            <label className="block text-sm">
              <div className="text-slate-700 font-medium mb-1.5">Klíčová slova</div>
              <input type="text" value={klicovaSlova} onChange={(e) => setKlicovaSlova(e.target.value)}
                placeholder="comma-separated, např. online, program, kurz"
                className="field w-full" />
              <div className="text-xs text-slate-400 mt-1">Cron matchuje název položky faktury proti těmto klíčovým slovům</div>
            </label>
          </div>

          <StupenEditor stupen={1} label="1. den po splatnosti" value={text1} onChange={setText1} allowedPlaceholders={allowedPlaceholders} />
          <StupenEditor stupen={3} label="3. den po splatnosti" value={text3} onChange={setText3} allowedPlaceholders={allowedPlaceholders} />
          <StupenEditor stupen={5} label="5. den po splatnosti (Slack alert + manuál)" value={text5} onChange={setText5} allowedPlaceholders={allowedPlaceholders} />

          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Zrušit
          </button>
          <button type="button"
            onClick={() => onSubmit({ action: "add", typ: typ.trim(), klicovaSlova: klicovaSlova.trim(), text1, text3, text5 })}
            disabled={submitting || !canSubmit}
            className="btn btn-primary">
            {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {submitting ? "Ukládám…" : "Přidat šablonu"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Inline editor pro existujici sablonu (po expand)
function EditRow({ sablona, allowedPlaceholders, onSave, onCancel, saving, canDelete, onDelete }) {
  const [klicovaSlova, setKlicovaSlova] = useState(sablona.klicovaSlova || "");
  const [text1, setText1] = useState(sablona.text1 || "");
  const [text3, setText3] = useState(sablona.text3 || "");
  const [text5, setText5] = useState(sablona.text5 || "");

  const dirty = klicovaSlova !== (sablona.klicovaSlova || "")
    || text1 !== (sablona.text1 || "")
    || text3 !== (sablona.text3 || "")
    || text5 !== (sablona.text5 || "");

  return (
    <div className="bg-brand-50/30 border-t border-brand-200 px-4 py-4 space-y-4">
      <div>
        <div className="text-xs text-slate-500 font-medium mb-1.5">Klíčová slova (comma-separated)</div>
        <input type="text" value={klicovaSlova} onChange={(e) => setKlicovaSlova(e.target.value)}
          placeholder="např. permanentka, 10t"
          className="field w-full" />
      </div>
      <StupenEditor stupen={1} label="1. den po splatnosti" value={text1} onChange={setText1} allowedPlaceholders={allowedPlaceholders} />
      <StupenEditor stupen={3} label="3. den po splatnosti" value={text3} onChange={setText3} allowedPlaceholders={allowedPlaceholders} />
      <StupenEditor stupen={5} label="5. den po splatnosti (Slack alert + manuál)" value={text5} onChange={setText5} allowedPlaceholders={allowedPlaceholders} />

      <div className="flex items-center justify-between gap-2">
        {canDelete ? (
          <button type="button" onClick={() => onDelete(sablona)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-rose-700 bg-white hover:bg-rose-50 border border-rose-300 rounded-xl transition">
            <Trash2 className="w-4 h-4" /> Smazat šablonu
          </button>
        ) : <div />}
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCancel}
            className="btn btn-secondary">
            Zavřít
          </button>
          <button type="button" disabled={!dirty || saving}
            onClick={() => onSave({ klicovaSlova, text1, text3, text5 })}
            className="btn btn-primary">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Ukládám…" : dirty ? "Uložit změny" : "Beze změn"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SmsSablony() {
  const { data, setData, loading, error, refetch, refreshing } = useApiData("/api/sms-sablony");
  const currentUser = getCurrentUser();
  const isOwner = currentUser?.role === "owner";

  const [search, setSearch] = useState("");
  const [expandedTyp, setExpandedTyp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const sablony = data?.sablony || [];
  const allowedPlaceholders = data?.allowedPlaceholders || ["jmeno", "castka", "cisloFaktury", "datumSplatnosti", "dniPoSplatnosti"];

  const filtered = useMemo(() => {
    if (!search) return sablony;
    const s = search.toLowerCase();
    return sablony.filter((sab) => {
      return (sab.typ || "").toLowerCase().includes(s)
        || (sab.klicovaSlova || "").toLowerCase().includes(s)
        || (sab.text1 || "").toLowerCase().includes(s)
        || (sab.text3 || "").toLowerCase().includes(s)
        || (sab.text5 || "").toLowerCase().includes(s);
    });
  }, [sablony, search]);

  const handleSaveUpdate = async (sablona, changes) => {
    setSaving(true); setSaveError(null);
    try {
      const res = await apiFetch("/api/sms-sablony", {
        method: "POST",
        body: JSON.stringify({
          action: "update",
          rowNum: sablona.rowNum,
          typ: sablona.typ,
          klicovaSlova: changes.klicovaSlova,
          text1: changes.text1,
          text3: changes.text3,
          text5: changes.text5,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      if (j.warnings && j.warnings.length > 0) {
        toast(`Uloženo, ale: ${j.warnings.join(" | ")}`, "info");
      }
      // Optimistic update v state
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sablony: prev.sablony.map((s) => s.rowNum === sablona.rowNum
            ? { ...s, ...changes }
            : s),
        };
      });
      setExpandedTyp(null);
    } catch (e) {
      setSaveError(e.message);
      toast(`Chyba uložení: ${e.message}`, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async (payload) => {
    setSubmitError(null);
    try {
      const res = await apiFetch("/api/sms-sablony", { method: "POST", body: JSON.stringify(payload) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      if (j.warnings && j.warnings.length > 0) toast(`Přidáno, ale: ${j.warnings.join(" | ")}`, "info");
      setAddModalOpen(false);
      refetch();
    } catch (e) {
      setSubmitError(e.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      const res = await apiFetch("/api/sms-sablony", {
        method: "POST",
        body: JSON.stringify({ action: "delete", typ: target.typ, rowNum: target.rowNum }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setExpandedTyp(null);
      refetch();
    } catch (e) {
      toast(`Chyba mazání: ${e.message}`, "error");
      refetch();
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <GradientHeader title="SMS šablony" icon={MessageSquare} loading={loading}>
        <Link to="/automatizace" className="btn btn-na-tmavem btn-sm">
          <ArrowLeft className="w-4 h-4" /> Zpět
        </Link>
        <button onClick={refetch} disabled={loading || refreshing}
          className="btn btn-na-tmavem btn-sm">
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Načítám…" : "Obnovit"}
        </button>
      </GradientHeader>

      <Card title={`Šablony (${filtered.length})`}>
        <div className="mb-4 bg-brand-50/40 border border-brand-200 rounded-xl p-4 text-sm text-slate-700 flex items-start gap-3">
          <InfoTip text="Cron smsUpominky běží denně v 9:00 CEST. Pro každou neuhrazenou fakturu spočítá dny po splatnosti, vybere šablonu podle klíčových slov v položce faktury (B), použije text dle stupně (1.den / 3.den / 5.den). Stupeň 5 SMS neposílá, jen Slack alert pro manuální řešení." />
          <div>
            <div className="font-medium text-slate-900 mb-1">Jak to funguje</div>
            <div className="text-xs leading-relaxed">
              Cron <code className="px-1 py-0.5 bg-white border border-slate-200 rounded text-brand-700">smsUpominky</code> matchuje typ šablony dle klíčových slov v názvu položky faktury.
              Stupně: <strong>1</strong> = 1.&nbsp;den po splatnosti, <strong>3</strong> = 3.&nbsp;den, <strong>5</strong> = 5.&nbsp;den (jen Slack alert, SMS se neposílá).
              Povolené placeholdery v textech: {allowedPlaceholders.map((ph) => (
                <code key={ph} className="px-1 py-0.5 mx-0.5 bg-white border border-slate-200 rounded text-brand-700 font-mono text-[11px]">{`{${ph}}`}</code>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Hledat v typech, klíčových slovech, textech…"
              className="field w-full pl-9 pr-3"
            />
          </div>
          <button type="button" onClick={() => { setSubmitError(null); setAddModalOpen(true); }}
            className="btn btn-primary">
            <Plus className="w-4 h-4" /> Přidat šablonu
          </button>
        </div>

        {loading && <div className="py-12 text-center text-slate-500 text-sm">Načítám šablony…</div>}
        {error && <div className="py-6 text-center text-rose-600 text-sm">Chyba: {error}</div>}
        {!loading && !error && filtered.length === 0 && (
          <div className="py-12 text-center text-slate-500 text-sm">Žádné šablony neodpovídají filtru.</div>
        )}

        {filtered.length > 0 && (
          <div className="space-y-2">
            {filtered.map((sab) => {
              const isExpanded = expandedTyp === sab.typ;
              return (
                <div key={`${sab.typ}-${sab.rowNum}`} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                  <button type="button"
                    onClick={() => setExpandedTyp(isExpanded ? null : sab.typ)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Tag className="w-4 h-4 text-brand-600 flex-shrink-0" />
                      <span className="font-semibold text-slate-900 truncate">{sab.typ}</span>
                      {sab.klicovaSlova && (
                        <span className="hidden md:inline text-xs text-slate-500 truncate">
                          <Hash className="w-3 h-3 inline mr-0.5" /> {sab.klicovaSlova}
                        </span>
                      )}
                    </div>
                    <div className="hidden lg:flex items-center gap-1.5 text-xs text-slate-500">
                      {[1, 3, 5].map((s) => {
                        const t = s === 1 ? sab.text1 : s === 3 ? sab.text3 : sab.text5;
                        const has = !!(t && t.trim());
                        return (
                          <span key={s} className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                            has ? "bg-brand-100 text-brand-700" : "bg-slate-100 text-slate-400"
                          }`} title={`Stupeň ${s}${has ? ", vyplněno" : ", prázdné"}`}>
                            {s}
                          </span>
                        );
                      })}
                    </div>
                    {isExpanded
                      ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                  </button>
                  {isExpanded && (
                    <EditRow sablona={sab} allowedPlaceholders={allowedPlaceholders}
                      onSave={(changes) => handleSaveUpdate(sab, changes)}
                      onCancel={() => setExpandedTyp(null)}
                      saving={saving}
                      canDelete={isOwner}
                      onDelete={(s) => setDeleteTarget(s)} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {addModalOpen && (
        <AddModal onClose={() => setAddModalOpen(false)} onSubmit={handleAdd}
          submitting={false} error={submitError}
          allowedPlaceholders={allowedPlaceholders}
          existingTypy={sablony.map((s) => s.typ)} />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title={`Smazat šablonu "${deleteTarget.typ}"?`}
          message={
            <div className="space-y-2">
              <div>Šablona se trvale smaže.</div>
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-2">
                Pokud má cron faktury které matchovaly tento typ, padne na <strong>fallback "Výchozí"</strong>. Před smazáním se ujisti, že "Výchozí" pokrývá všechny případy.
              </div>
            </div>
          }
          confirmLabel="Ano, smazat"
          cancelLabel="Zrušit"
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
